import { useState } from 'react';
import { Check, X, ChevronDown, MapPin, Utensils, Hotel, Camera, Plane, Car, Info, IndianRupee } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { formatPrice, cn } from '../../lib/utils';

const mealIcons = {
  'Breakfast': '🍳',
  'Lunch': '🍱',
  'Dinner': '🍽️',
};

const activityIcons = {
  'Sightseeing': <Camera className="w-4 h-4" />,
  'Transfer': <Car className="w-4 h-4" />,
  'Flight': <Plane className="w-4 h-4" />,
  'Hotel': <Hotel className="w-4 h-4" />,
};

export function TabSection({ tour }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedDays, setExpandedDays] = useState(['day-0']);

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
      <TabsList className="w-full flex flex-wrap h-auto bg-gray-100 rounded-xl p-1 gap-1 mb-8">
        {['overview', 'itinerary', 'inclusions', 'exclusions', 'pricing'].map((tab) => (
          <TabsTrigger
            key={tab}
            value={tab}
            className={cn(
              'flex-1 min-w-[100px] py-3 rounded-lg text-sm font-medium capitalize transition-all',
              'data-[state=active]:bg-white data-[state=active]:text-[#FF6B35] data-[state=active]:shadow-sm'
            )}
          >
            {tab}
          </TabsTrigger>
        ))}
      </TabsList>

      {/* Overview Tab */}
      <TabsContent value="overview" className="mt-0">
        <div className="space-y-8">
          {/* Description */}
          <div>
            <h3 
              className="text-xl font-semibold mb-4 text-[#1A1A1A]"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              About This Tour
            </h3>
            <p className="text-[#4A4A4A] leading-relaxed text-lg">
              {tour.description || 'Experience an unforgettable journey through stunning landscapes and rich cultural heritage. This carefully curated tour offers the perfect blend of adventure, relaxation, and discovery.'}
            </p>
          </div>

          {/* Highlights */}
          {tour.highlights && tour.highlights.length > 0 && (
            <div>
              <h3 
                className="text-xl font-semibold mb-4 text-[#1A1A1A]"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Tour Highlights
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {tour.highlights.map((highlight, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-3 bg-[#FF6B35]/5 rounded-xl p-4"
                  >
                    <div className="w-6 h-6 bg-[#FF6B35] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-[#1A1A1A]">{highlight}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quick Facts */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Best Time', value: tour.best_season || 'Year Round', icon: '🌤️' },
              { label: 'Tour Type', value: tour.category, icon: '✈️' },
              { label: 'Difficulty', value: tour.difficulty || 'Easy', icon: '🏃' },
              { label: 'Languages', value: 'English, Hindi', icon: '🗣️' },
            ].map((fact, idx) => (
              <div key={idx} className="bg-gray-50 rounded-xl p-4 text-center">
                <span className="text-2xl mb-2 block">{fact.icon}</span>
                <p className="text-sm text-[#4A4A4A]">{fact.label}</p>
                <p className="font-semibold text-[#1A1A1A]">{fact.value}</p>
              </div>
            ))}
          </div>
        </div>
      </TabsContent>

      {/* Itinerary Tab */}
      <TabsContent value="itinerary" className="mt-0">
        <div>
          <h3 
            className="text-xl font-semibold mb-6 text-[#1A1A1A]"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Day-by-Day Itinerary
          </h3>
          
          <Accordion 
            type="multiple" 
            value={expandedDays} 
            onValueChange={setExpandedDays}
            className="space-y-4"
          >
            {(tour.itinerary && tour.itinerary.length > 0 ? tour.itinerary : [
              { day: 1, title: 'Arrival & Welcome', description: 'Arrive at your destination and check into your hotel. Evening at leisure to explore the surroundings.', meals: ['Dinner'], highlights: ['Airport Transfer', 'Hotel Check-in'] },
              { day: 2, title: 'Full Day Sightseeing', description: 'After breakfast, embark on a full day sightseeing tour covering major attractions.', meals: ['Breakfast', 'Lunch'], highlights: ['Local Sightseeing', 'Cultural Experience'] },
              { day: 3, title: 'Adventure Activities', description: 'Experience thrilling adventure activities and explore hidden gems of the destination.', meals: ['Breakfast', 'Dinner'], highlights: ['Adventure Sports', 'Nature Walk'] },
              { day: 4, title: 'Departure', description: 'After breakfast, check out from the hotel and transfer to airport for your onward journey.', meals: ['Breakfast'], highlights: ['Airport Transfer'] },
            ]).map((day, index) => (
              <AccordionItem
                key={index}
                value={`day-${index}`}
                className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm"
              >
                <AccordionTrigger className="hover:no-underline px-6 py-5">
                  <div className="flex items-center gap-4 text-left w-full">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#FF6B35] to-[#E85A2D] rounded-xl flex flex-col items-center justify-center text-white flex-shrink-0">
                      <span className="text-xs font-medium">Day</span>
                      <span className="text-xl font-bold">{day.day}</span>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-[#1A1A1A] text-lg">{day.title}</h4>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {day.meals?.map((meal, i) => (
                          <span key={i} className="text-xs text-[#4A4A4A] bg-gray-100 px-2 py-1 rounded-full">
                            {mealIcons[meal] || '🍽️'} {meal}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <div className="ml-[4.5rem] space-y-4">
                    <p className="text-[#4A4A4A] leading-relaxed">{day.description}</p>
                    
                    {day.highlights && day.highlights.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {day.highlights.map((highlight, i) => (
                          <Badge
                            key={i}
                            variant="outline"
                            className="bg-[#FDFBF7] border-[#FF6B35]/20 text-[#FF6B35]"
                          >
                            {highlight}
                          </Badge>
                        ))}
                      </div>
                    )}

                    {day.accommodation && (
                      <div className="flex items-center gap-2 text-sm text-[#4A4A4A] bg-blue-50 rounded-lg p-3">
                        <Hotel className="w-4 h-4 text-blue-500" />
                        <span>Stay: {day.accommodation}</span>
                      </div>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <Button
            variant="outline"
            onClick={() => setExpandedDays(expandedDays.length > 0 ? [] : tour.itinerary?.map((_, i) => `day-${i}`) || ['day-0', 'day-1', 'day-2', 'day-3'])}
            className="mt-6 w-full rounded-xl border-[#004E64] text-[#004E64]"
          >
            {expandedDays.length > 0 ? 'Collapse All Days' : 'Expand All Days'}
          </Button>
        </div>
      </TabsContent>

      {/* Inclusions Tab */}
      <TabsContent value="inclusions" className="mt-0">
        <div className="bg-[#2A9D8F]/5 rounded-2xl p-6 md:p-8">
          <h3 
            className="text-xl font-semibold mb-6 text-[#1A1A1A] flex items-center gap-2"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            <Check className="w-6 h-6 text-[#2A9D8F]" />
            What&apos;s Included
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(tour.inclusions && tour.inclusions.length > 0 ? tour.inclusions : [
              'Accommodation in 3/4 star hotels',
              'Daily breakfast at hotel',
              'All transfers and sightseeing by AC vehicle',
              'Professional English speaking guide',
              'All applicable taxes and fees',
              'Welcome drink on arrival',
              'Entrance fees to monuments',
              '24/7 customer support'
            ]).map((item, idx) => (
              <div
                key={idx}
                className="flex items-start gap-3 bg-white rounded-xl p-4 shadow-sm"
              >
                <div className="w-6 h-6 bg-[#2A9D8F] rounded-full flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-white" />
                </div>
                <span className="text-[#1A1A1A]">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </TabsContent>

      {/* Exclusions Tab */}
      <TabsContent value="exclusions" className="mt-0">
        <div className="bg-[#E76F51]/5 rounded-2xl p-6 md:p-8">
          <h3 
            className="text-xl font-semibold mb-6 text-[#1A1A1A] flex items-center gap-2"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            <X className="w-6 h-6 text-[#E76F51]" />
            What&apos;s Not Included
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(tour.exclusions && tour.exclusions.length > 0 ? tour.exclusions : [
              'International/Domestic airfare',
              'Personal expenses and tips',
              'Travel insurance',
              'Meals not mentioned in itinerary',
              'Optional activities and excursions',
              'Visa fees (if applicable)',
              'Camera/Video fees at monuments',
              'Anything not mentioned in inclusions'
            ]).map((item, idx) => (
              <div
                key={idx}
                className="flex items-start gap-3 bg-white rounded-xl p-4 shadow-sm"
              >
                <div className="w-6 h-6 bg-[#E76F51] rounded-full flex items-center justify-center flex-shrink-0">
                  <X className="w-4 h-4 text-white" />
                </div>
                <span className="text-[#1A1A1A]">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </TabsContent>

      {/* Pricing Tab */}
      <TabsContent value="pricing" className="mt-0">
        <div className="space-y-6">
          <h3 
            className="text-xl font-semibold mb-4 text-[#1A1A1A]"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Pricing Details
          </h3>

          {/* Price Table */}
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#004E64] text-white">
                <tr>
                  <th className="px-6 py-4 text-left font-semibold">Room Type</th>
                  <th className="px-6 py-4 text-center font-semibold">Per Person</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-100">
                  <td className="px-6 py-4">
                    <div className="font-medium text-[#1A1A1A]">Double Sharing</div>
                    <div className="text-sm text-[#4A4A4A]">2 adults per room</div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="font-bold text-[#004E64] text-xl">
                      {formatPrice(tour.discounted_price || tour.price)}
                    </div>
                    {tour.discounted_price && (
                      <div className="text-sm text-[#4A4A4A] line-through">
                        {formatPrice(tour.price)}
                      </div>
                    )}
                  </td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="px-6 py-4">
                    <div className="font-medium text-[#1A1A1A]">Triple Sharing</div>
                    <div className="text-sm text-[#4A4A4A]">3 adults per room</div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="font-bold text-[#004E64] text-xl">
                      {formatPrice(Math.round((tour.discounted_price || tour.price) * 0.9))}
                    </div>
                    <div className="text-xs text-[#2A9D8F]">10% off</div>
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4">
                    <div className="font-medium text-[#1A1A1A]">Single Occupancy</div>
                    <div className="text-sm text-[#4A4A4A]">1 adult per room</div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="font-bold text-[#004E64] text-xl">
                      {formatPrice(Math.round((tour.discounted_price || tour.price) * 1.4))}
                    </div>
                    <div className="text-xs text-[#4A4A4A]">+40% supplement</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Child Policy */}
          <div className="bg-blue-50 rounded-2xl p-6">
            <h4 className="font-semibold text-[#1A1A1A] mb-3 flex items-center gap-2">
              <Info className="w-5 h-5 text-blue-500" />
              Child Policy
            </h4>
            <ul className="space-y-2 text-[#4A4A4A]">
              <li>• Child below 5 years: Free (without extra bed)</li>
              <li>• Child 5-11 years: 50% of adult price (with extra bed)</li>
              <li>• Child above 11 years: Adult price</li>
            </ul>
          </div>

          {/* Payment Terms */}
          <div className="bg-gray-50 rounded-2xl p-6">
            <h4 className="font-semibold text-[#1A1A1A] mb-3">Payment Terms</h4>
            <ul className="space-y-2 text-[#4A4A4A]">
              <li>• 25% advance at the time of booking</li>
              <li>• 50% payment 15 days before departure</li>
              <li>• Balance 25% on arrival</li>
            </ul>
          </div>
        </div>
      </TabsContent>
    </Tabs>
  );
}
